import React, { useState, useEffect, useCallback } from 'react';
import { Brain, ArrowRight, ArrowLeft, CheckCircle, Save, Clock, Plus, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, Button, Input, Alert, ProgressBar } from '../design-system';
import { apiPost } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

// Task-specific configuration for Reality Check Moment
export const TaskRealityCheckConfig = {
  steps: [
    {
      id: 'step1',
      title: 'Pause and Name the Worry',
      description: 'What\'s the worst thing your mind is predicting right now?',
      subtitle: 'Take a breath. Let\'s write it down — not to judge it, but to understand it.',
      type: 'text_input',
      required: true,
      placeholder: 'Write your feared outcome in one clear sentence...'
    },
    {
      id: 'step2',
      title: 'Facts That Support It',
      description: 'List up to three facts that make this outcome feel real or possible.',
      subtitle: 'Facts are things you could prove or others could see.',
      type: 'fact_list',
      required: true,
      maxFacts: 3,
      placeholder: 'Add a fact that supports your worry...'
    },
    {
      id: 'step3',
      title: 'Facts That Challenge It',
      description: 'Now, what are three facts that make this outcome less likely?',
      subtitle: 'Think of times you worried before and it turned out okay.',
      type: 'fact_list',
      required: true,
      maxFacts: 3,
      placeholder: 'Add a fact that challenges your worry...'
    },
    {
      id: 'step4',
      title: 'Write a Balanced View',
      description: 'Using both lists above, write one balanced statement that includes both sides of the story.',
      subtitle: 'Include both supporting and challenging facts in your statement.',
      type: 'balanced_text',
      required: true,
      placeholder: 'Write a balanced statement that includes both sides...',
      minLength: 20
    },
    {
      id: 'step5',
      title: 'Summary',
      description: 'Review your reflection and celebrate practicing balanced thinking.',
      subtitle: 'You just practiced balanced thinking — every time you do this, your brain learns to calm the storm faster.',
      type: 'summary',
      required: false
    }
  ]
};

const TaskRealityCheck = ({ task, taskType, session, onComplete }) => {
  const { user, userInfo } = useAuth();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [taskData, setTaskData] = useState({
    fearedOutcome: '',
    supportingFacts: [],
    challengingFacts: [],
    balancedStatement: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasLoadedProgress, setHasLoadedProgress] = useState(false);
  const [showFacts, setShowFacts] = useState(false);
  const [newFact, setNewFact] = useState('');

  const steps = TaskRealityCheckConfig.steps;
  const currentStep = steps[currentStepIndex];

  // Auto-save functionality
  const saveProgress = useCallback(async (taskData) => {
    if (!session?.sessionId) return;

    console.log('🔄 Attempting to save progress:', {
      sessionId: session.sessionId,
      taskData: taskData,
      currentStepIndex: currentStepIndex
    });

    setIsSaving(true);
    try {
      const requestBody = {
        sessionId: session.sessionId,
        stepData: taskData,
        currentStepIndex: currentStepIndex
      };

      console.log('📤 Sending request to API:', requestBody);

      const data = await apiPost(
        '/.netlify/functions/realityCheckTaskApi',
        requestBody,
        userInfo?.userId
      );

      setLastSaved(new Date());
      
      console.log('💾 Reality Check progress saved:', {
        taskData: taskData,
        currentStepIndex: currentStepIndex,
        isCompleted: data.isCompleted
      });
      
      // Check if task is completed
      if (data.isCompleted) {
        console.log('✅ Task auto-completed:', data.completionData);
        onComplete(data.completionData);
      }
    } catch (error) {
      console.error('Error saving progress:', error);
    } finally {
      setIsSaving(false);
    }
  }, [session?.sessionId, currentStepIndex, onComplete, user, userInfo?.userId]);

  // Debounced auto-save
  const debouncedSave = useCallback(
    (() => {
      let timeoutId;
      return (taskData) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          saveProgress(taskData);
        }, 3000); // 3 seconds
      };
    })(),
    [saveProgress]
  );

  // Load progress from session
  useEffect(() => {
    if (!session || hasLoadedProgress) return;
    
    setHasLoadedProgress(true);
    
    console.log('🔄 Loading Reality Check progress:', {
      sessionData: session.sessionData,
      hasTaskData: !!session.sessionData?.taskData,
      currentStepIndex: session.sessionData?.currentStepIndex
    });
    
    // Load existing data if session has data
    if (session.sessionData?.taskData) {
      console.log('📥 Loading saved task data:', session.sessionData.taskData);
      setTaskData(session.sessionData.taskData);
      setCurrentStepIndex(session.sessionData.currentStepIndex || 0);
    } else {
      console.log('⚠️ No saved task data found');
    }
  }, [session, hasLoadedProgress]);

  const handleDataChange = (field, value) => {
    const newTaskData = { ...taskData, [field]: value };
    setTaskData(newTaskData);
    
    // Auto-save with debouncing
    debouncedSave(newTaskData);
  };

  const addFact = (type, fact) => {
    if (!fact.trim()) return;
    
    const field = type === 'supporting' ? 'supportingFacts' : 'challengingFacts';
    const maxFacts = 3;
    
    if (taskData[field].length >= maxFacts) {
      toast.error(`You can only add up to ${maxFacts} facts`);
      return;
    }
    
    handleDataChange(field, [...taskData[field], fact.trim()]);
  };

  const removeFact = (type, index) => {
    const field = type === 'supporting' ? 'supportingFacts' : 'challengingFacts';
    const newFacts = taskData[field].filter((_, i) => i !== index);
    handleDataChange(field, newFacts);
  };

  const handleNext = async () => {
    // Validate current step
    if (!validateCurrentStep()) {
      return;
    }

    // Save current progress
    try {
      await saveProgress(taskData);
    } catch (error) {
      console.error('Error saving progress on next:', error);
    }

    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    }
  };

  const handlePrevious = async () => {
    // Save current progress
    try {
      await saveProgress(taskData);
    } catch (error) {
      console.error('Error saving progress on previous:', error);
    }

    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  const validateCurrentStep = () => {
    if (!currentStep) {
      return false;
    }
    switch (currentStep.id) {
      case 'step1':
        return taskData.fearedOutcome.trim().length > 0;
      case 'step2':
        return taskData.supportingFacts.length > 0;
      case 'step3':
        return taskData.challengingFacts.length > 0;
      case 'step4':
        return taskData.balancedStatement.trim().length >= 20;
      default:
        return true;
    }
  };

  const handleComplete = async () => {
    console.log('🎯 Manual completion triggered:', {
      taskData: taskData,
      currentStepIndex: currentStepIndex
    });
    
    setIsSubmitting(true);
    try {
      // First, save completion data to the session
      const completionData = {
        fearedOutcome: taskData.fearedOutcome,
        supportingFacts: taskData.supportingFacts,
        challengingFacts: taskData.challengingFacts,
        balancedStatement: taskData.balancedStatement,
        completedAt: new Date().toISOString()
      };

      // Save completion data to session
      await apiPost(
        '/.netlify/functions/realityCheckTaskApi',
        {
          sessionId: session.sessionId,
          action: 'complete',
          completionData: completionData
        },
        userInfo?.userId
      );

      // Then complete the task session
      await apiPost(
        '/.netlify/functions/complete-task-session',
        {
          sessionId: session.sessionId,
          taskId: task.taskId
        },
        userInfo?.userId
      );

      // Analytics tracking is now handled automatically in TaskPage
      // No need to track here - TaskPage will track all task completions

      toast.success('Reality Check completed!');
      onComplete();
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep.type) {
      case 'text_input':
        return (
          <div className="space-y-4">
            <Input
              type="textarea"
              value={taskData.fearedOutcome}
              onChange={(e) => handleDataChange('fearedOutcome', e.target.value)}
              placeholder={currentStep.placeholder}
              rows={4}
              className="resize-none"
            />
          </div>
        );

      case 'fact_list':
        const isSupporting = currentStep.id === 'step2';
        const facts = isSupporting ? taskData.supportingFacts : taskData.challengingFacts;

        return (
          <div className="space-y-4">
            {/* Add new fact */}
            <div className="flex space-x-2">
              <Input
                type="text"
                value={newFact}
                onChange={(e) => setNewFact(e.target.value)}
                placeholder={currentStep.placeholder}
                className="flex-1"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newFact.trim()) {
                    addFact(isSupporting ? 'supporting' : 'challenging', newFact);
                    setNewFact('');
                  }
                }}
              />
              <Button
                onClick={() => {
                  if (newFact.trim()) {
                    addFact(isSupporting ? 'supporting' : 'challenging', newFact);
                    setNewFact('');
                  }
                }}
                disabled={!newFact.trim() || facts.length >= 3}
                variant="primary"
                size="medium"
                className="flex items-center"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>

            {/* Display facts */}
            <div className="space-y-2">
              {facts.map((fact, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <span className="text-sm text-gray-700 flex-1">{fact}</span>
                  <Button
                    onClick={() => removeFact(isSupporting ? 'supporting' : 'challenging', index)}
                    variant="ghost"
                    size="small"
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            {facts.length === 0 && (
              <Alert variant="info">
                <p className="text-sm text-gray-600">Add at least one fact to continue.</p>
              </Alert>
            )}
          </div>
        );

      case 'balanced_text':
        return (
          <div className="space-y-4">
            {/* Show previous facts */}
            <div>
              <button
                onClick={() => setShowFacts(!showFacts)}
                className="flex items-center text-sm text-blue-600 hover:text-blue-700 transition-colors mb-2"
              >
                {showFacts ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
                {showFacts ? 'Hide' : 'Show'} previous facts
              </button>
              
              {showFacts && (
                <div className="space-y-3 mb-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Supporting facts:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {taskData.supportingFacts.map((fact, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-red-500 mr-2">•</span>
                          {fact}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Challenging facts:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {taskData.challengingFacts.map((fact, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-green-500 mr-2">•</span>
                          {fact}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            <Input
              type="textarea"
              value={taskData.balancedStatement}
              onChange={(e) => handleDataChange('balancedStatement', e.target.value)}
              placeholder={currentStep.placeholder}
              rows={6}
              className="resize-none"
            />
            
            <div className="text-sm text-gray-500">
              {taskData.balancedStatement.length}/20 characters minimum
            </div>
          </div>
        );

      case 'summary':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="text-4xl sm:text-6xl mb-4">🎉</div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Great job!</h3>
              <p className="text-sm sm:text-base text-gray-600">You just practiced balanced thinking.</p>
            </div>

            <Card variant="elevated" padding="large">
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Feared outcome:</h4>
                  <p className="text-sm sm:text-base text-gray-700 italic break-words">"{taskData.fearedOutcome}"</p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Balanced statement:</h4>
                  <p className="text-sm sm:text-base text-gray-700 italic break-words">"{taskData.balancedStatement}"</p>
                </div>
              </div>
            </Card>

            <Alert variant="success">
              <p className="text-xs sm:text-sm">
                <strong>Well done!</strong> Every time you do this, your brain learns to calm the storm faster. 
                You've completed 1 of your tasks this week.
              </p>
            </Alert>
          </div>
        );

      default:
        return null;
    }
  };

  if (!currentStep) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Brain className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Loading reality check...</p>
        </div>
      </div>
    );
  }

  const isLastStep = currentStepIndex === steps.length - 1;
  const isFirstStep = currentStepIndex === 0;
  const canProceed = validateCurrentStep();

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row items-center justify-center mb-4 space-y-3 sm:space-y-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center sm:mr-4">
            <Brain className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
          </div>
          <div className="text-center sm:text-left">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{taskType.pageTitle}</h1>
            <p className="text-sm sm:text-base text-gray-600">Reality Check Moment</p>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <ProgressBar
          current={currentStepIndex + 1}
          total={steps.length}
          variant="purple"
          size="medium"
          showPercentage={true}
          label={`Step ${currentStepIndex + 1} of ${steps.length}`}
        />
      </div>

      {/* Current Step */}
      <Card variant="elevated" padding="large" className="mb-6">
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {currentStep.title}
            </h2>
            <p className="text-gray-700 mb-2">{currentStep.description}</p>
            {currentStep.subtitle && (
              <p className="text-sm text-gray-600 italic">{currentStep.subtitle}</p>
            )}
          </div>

          {renderStepContent()}
        </div>
      </Card>

      {/* Navigation */}
      <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
        <Button
          onClick={handlePrevious}
          disabled={isFirstStep}
          variant="ghost"
          size="medium"
          className="flex items-center w-full sm:w-auto"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
          <Button
            onClick={() => saveProgress(taskData)}
            disabled={isSaving}
            variant="ghost"
            size="medium"
            className="flex items-center w-full sm:w-auto"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Progress'}
          </Button>

          {isLastStep ? (
            <Button
              onClick={handleComplete}
              disabled={isSubmitting}
              variant="success"
              size="medium"
              className="flex items-center w-full sm:w-auto"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {isSubmitting ? 'Completing...' : 'Finish Reflection'}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              disabled={!canProceed}
              variant="primary"
              size="medium"
              className="flex items-center w-full sm:w-auto"
            >
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>

      {/* Save Status */}
      {lastSaved && (
        <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
          <Clock className="h-4 w-4 mr-2" />
          Last saved: {lastSaved.toLocaleTimeString()}
        </div>
      )}

      {/* Helpful Tips */}
      <Alert variant="info" className="mt-8">
        <h3 className="text-sm font-medium text-purple-800 mb-2">💡 Reality Check Tips</h3>
        <ul className="text-sm text-purple-700 space-y-1">
          <li>• Be honest about your thoughts and feelings - there are no wrong answers</li>
          <li>• Take your time with each step - this is about slowing down your thoughts</li>
          <li>• Your progress is automatically saved as you go</li>
          <li>• You can navigate between steps freely</li>
          <li>• This exercise helps you practice balanced thinking</li>
        </ul>
      </Alert>
    </div>
  );
};

// Add static methods to the component for Summary and ResultsSummary
TaskRealityCheck.Summary = ({ task, session }) => {
  const stepCount = session?.sessionData?.currentStepIndex ? session.sessionData.currentStepIndex + 1 : 0;
  const totalSteps = TaskRealityCheckConfig.steps.length;
  
  return (
    <div className="reality-check-summary">
      <div className="flex items-center space-x-2">
        <Brain className="h-4 w-4 text-purple-600" />
        <span className="text-xs text-gray-500">
          {stepCount}/{totalSteps} steps completed
        </span>
      </div>
    </div>
  );
};

TaskRealityCheck.ResultsSummary = ({ task, completionData, patientName }) => {
  if (!completionData) return null;
  
  return (
    <div className="reality-check-results-summary">
      <Card variant="elevated" padding="large" className="mb-4">
        <h3 className="font-medium text-gray-900 mb-4">Reality Check Results - {patientName}</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-gray-700 mb-2">Feared outcome:</h4>
            <p className="text-gray-600 italic">"{completionData.fearedOutcome}"</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-red-50 p-3 rounded-lg">
              <h5 className="font-medium text-red-800 mb-2">Supporting facts:</h5>
              <ul className="text-sm text-red-700 space-y-1">
                {completionData.supportingFacts?.map((fact, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    {fact}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-green-50 p-3 rounded-lg">
              <h5 className="font-medium text-green-800 mb-2">Challenging facts:</h5>
              <ul className="text-sm text-green-700 space-y-1">
                {completionData.challengingFacts?.map((fact, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    {fact}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-700 mb-2">Balanced statement:</h4>
            <p className="text-sm sm:text-base text-gray-600 italic break-words">"{completionData.balancedStatement}"</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Add static method for task assignment form
TaskRealityCheck.AssignTaskForm = ({ formData, setFormData, taskType }) => {
  // No custom form needed - return null
  return null;
};

export default TaskRealityCheck;
